<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
const { t } = useI18n()

/**
 * Base modal on the native <dialog> element, styled from the Sofax template.
 *
 * The dialog carries `.sofax` itself: it is also opened from the CV editor,
 * whose layout does not, and the design system must not depend on where the
 * dialog happens to be rendered. Header (title + optional subtitle), a
 * scrollable body and a footer bar are all optional.
 */
interface Props {
  modelValue: boolean
  title?: string
  /** One line under the title, in muted text. */
  subtitle?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl'
  header?: boolean
  footer?: boolean
  dialogClass?: string
  contentClass?: string
  closable?: boolean
  /** On phones the dialog takes the whole screen, for long reads. */
  fullscreenMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  size: 'md',
  header: true,
  footer: true,
  dialogClass: '',
  contentClass: '',
  closable: true,
  fullscreenMobile: false
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const dialogRef = ref<HTMLDialogElement | null>(null)

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl'
} as const

onMounted(() => {
  if (props.modelValue) {
    dialogRef.value?.showModal()
  }
})

watch(() => props.modelValue, (open: boolean) => {
  if (open) {
    dialogRef.value?.showModal()
  } else {
    dialogRef.value?.close()
  }
})

function close() {
  emit('update:modelValue', false)
}

// A click that lands on the <dialog> itself, not its panel, is the backdrop
function handleBackdropClick(event: MouseEvent) {
  if (event.target === dialogRef.value && props.closable) {
    close()
  }
}

// The browser closes the dialog on Escape on its own; keep v-model in sync
function handleCancel(event: Event) {
  if (!props.closable) {
    event.preventDefault()
    return
  }
  close()
}

const showHeader = computed(() => props.header && !!props.title)
</script>

<template>
  <dialog
    ref="dialogRef"
    class="sofax-dialog sofax m-auto w-[calc(100vw-2rem)] overflow-visible bg-transparent p-0 text-base"
    :class="[sizes[props.size], props.dialogClass, { 'max-sm:m-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-screen max-sm:max-w-[100vw]': props.fullscreenMobile }]"
    :aria-label="props.title || undefined"
    @click="handleBackdropClick"
    @cancel="handleCancel"
  >
    <div
      class="relative flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-[0.625rem] bg-white shadow-lift sm:max-h-[88vh] dark:bg-gray-900 dark:ring-1 dark:ring-gray-700"
      :class="{ 'max-sm:h-full max-sm:max-h-full max-sm:rounded-none': props.fullscreenMobile }"
    >
      <!-- Header -->
      <div
        v-if="showHeader"
        class="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4 sm:px-8 sm:py-5 dark:border-gray-700"
      >
        <div class="min-w-0 pt-1">
          <h3 class="!text-xl leading-tight md:!text-2xl">{{ title }}</h3>
          <p v-if="subtitle" class="mt-1 !text-sm text-muted dark:text-gray-400">{{ subtitle }}</p>
        </div>
        <button
          v-if="props.closable"
          type="button"
          class="social-link -mr-1 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          :aria-label="t('common.close')"
          :title="t('common.close')"
          @click="close"
        >
          <i class="bi bi-x-lg text-base" aria-hidden="true" />
        </button>
      </div>

      <!-- Close button when there is no header bar -->
      <button
        v-else-if="props.closable"
        type="button"
        class="social-link absolute right-3 top-3 z-10 shadow-lift focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        :aria-label="t('common.close')"
        :title="t('common.close')"
        @click="close"
      >
        <i class="bi bi-x-lg text-base" aria-hidden="true" />
      </button>

      <!-- Body -->
      <div class="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6" :class="props.contentClass">
        <slot />
      </div>

      <!-- Footer -->
      <div
        v-if="footer"
        class="shrink-0 border-t border-line bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-8 sm:py-4 dark:border-gray-700 dark:bg-gray-800/60"
      >
        <slot name="footer">
          <div class="flex justify-end">
            <SofaxBtn variant="dark" class="btn-sm" :label="t('common.close')" @click="close" />
          </div>
        </slot>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
.sofax-dialog {
  color: inherit;
}

.sofax-dialog::backdrop {
  background: rgb(14 14 14 / 0.6);
  backdrop-filter: blur(4px);
}

/* Short rise-in on open; skipped when the visitor asks for less motion. */
@media (prefers-reduced-motion: no-preference) {
  .sofax-dialog[open] {
    animation: sofax-dialog-in 180ms ease-out;
  }

  .sofax-dialog[open]::backdrop {
    animation: sofax-backdrop-in 180ms ease-out;
  }
}

@keyframes sofax-dialog-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
}

@keyframes sofax-backdrop-in {
  from {
    opacity: 0;
  }
}
</style>
